import React, { useMemo, useCallback, useState, useEffect } from 'react'
import injectStyles from 'react-jss'
import { useRouteMatch } from 'react-router-dom'

import Sync from 'Containers/Sync'
import { SidebarWrapper } from 'Components/Sidebar/SidebarWrapper'
import GlobalSearch from '../../Components/GlobalSearch/container/GlobalSearch'

import { PATHS } from 'Config/Routing/paths'
import { generateSuggestionURL } from '../../Utils/GlobalSearch/GlobalSearchUtils'
import { isSmallScreen } from 'Utils/Misc'
import { authorization } from 'Module/Auth/Auth'
import { apiClient } from '../../Module/API/Client'

import packageJson from '../../../package.json'

import ClientsIcon from 'Assets/Images/icons/clients'
import CitiesIcon from 'Assets/Images/icons/cities'
import CountriesIcon from 'Assets/Images/icons/countries'
import CampaignsIcon from 'Assets/Images/icons/campaigns'
import UserManagementIcon from 'Assets/Images/icons/userManagement'
import SettingsIcon from 'Assets/Images/icons/settings'
import ChatIcon from 'Assets/Images/icons/chat'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import SidebarCreativeIcon from 'Assets/Images/icons/sidebarCreative'
import DMWorkflowsIcon from 'Assets/Images/icons/dmWorkflows'

import SidebarButton from './components/SidebarButton'
import If from './components/If'
import IfElse from './components/IfElse'

import styles from './styles'
import { CONFIG } from '../../Config/config'

const isSidebarOpen = (() => {
  if (!localStorage.getItem('sidebarState')) return !isSmallScreen()
  return localStorage.getItem('sidebarState') === 'open'
})()

const Sidebar = ({ children, classes }) => {
  const [isOpen, setIsOpen] = useState(isSidebarOpen)
  const { path } = useRouteMatch()

  useEffect(() => {
    if (!authorization.isLogged()) {
      // eslint-disable-next-line no-console
      console.log('## debug, sidebar useEffect, redirect to /errors?type=auth')
      window.location.href = '/errors?type=auth'
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('sidebarState', isOpen ? 'open' : 'close')
  }, [isOpen])

  const {
    isShowGlobalSearch,
    isShowClients,
    isShowCampaigns,
    isShowCountries,
    isShowCities,
    isShowUserManagement,
    isShowSettings,
    // isShowChat,
    isShowChatV2,
    showHelpCenter,
    isShowDesignManagement,
    isShowDMWorkflows,
  } = useMemo(() => {
    const isSpy = authorization.isSpy()
    const isAdmin = authorization.isAdmin()
    const isCityManager = authorization.isCityManager()
    const isClient = authorization.isClient()
    const isClientAdmin = authorization.isClientAdmin()
    const isSelfServeEnabled = authorization.isSelfServeEnabled()
    const isCityManagerAssistant = authorization.isCityManagerAssistant()
    return {
      isShowGlobalSearch: !isSpy && (isAdmin || isCityManager),
      isShowClients: !isSpy && !isClient && !isCityManagerAssistant,
      isShowCampaigns: !isSpy && (isClient || isCityManager || isAdmin),
      isShowCountries: isAdmin,
      isShowCities: isCityManager || isAdmin || isSpy,
      isShowUserManagement: isAdmin || isClientAdmin,
      isShowSettings:
        (isAdmin || isCityManager) && !isSpy && !isCityManagerAssistant,
      // isShowChat: !isSpy && (isCityManager || isAdmin),
      isShowChatV2: !isSpy && (isCityManager || isAdmin),
      showHelpCenter: isSelfServeEnabled,
      isShowDesignManagement: isAdmin,
      isShowDMWorkflows: isAdmin,
    }
  }, [])

  const onToggle = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen])

  const searchRequestHandler = useCallback(async searchedValue => {
    const response = await apiClient.globalSearch(searchedValue)
    return response.json()
  }, [])

  return (
    <SidebarWrapper content={children} onToggle={onToggle} open={isOpen}>
      <IfElse condition={isShowGlobalSearch}>
        <GlobalSearch
          onSuggestionSelected={suggestion => {
            window.open(generateSuggestionURL(suggestion), '_blank')
          }}
          searchRequestHandler={searchRequestHandler}
          showButton={true}
        />
        <span className={classes.border} />
      </IfElse>
      <If condition={isShowClients}>
        <SidebarButton
          id="clients-button"
          icon={<ClientsIcon />}
          width={'22px'}
          to={`${path}${PATHS.dashboard.clients}`}
        >
          Clients
        </SidebarButton>
      </If>
      <If condition={isShowCampaigns}>
        <SidebarButton
          id="campaigns-button"
          icon={<CampaignsIcon />}
          width={'16px'}
          to={`${path}${PATHS.dashboard.campaigns}?Status=Active`}
        >
          Campaigns
        </SidebarButton>
      </If>
      <If condition={isShowDMWorkflows}>
        <SidebarButton
          id="dm-workflows-button"
          icon={<DMWorkflowsIcon />}
          width={'20px'}
          to={{
            pathname: `${CONFIG.NEXT_BASE_URL}${PATHS.dashboard.dashboard}${
              PATHS.dashboard.directMailWorkflows
            }`,
          }}
          target="_blank"
        >
          DM Workflows
        </SidebarButton>
      </If>
      <If condition={isShowCountries}>
        <SidebarButton
          id="countries-button"
          icon={<CountriesIcon />}
          width={'18px'}
          to={`${path}${PATHS.dashboard.countries}`}
        >
          Countries
        </SidebarButton>
      </If>
      <If condition={isShowCities}>
        <SidebarButton
          id="cities-button"
          icon={<CitiesIcon />}
          width={'18px'}
          to={`${path}${PATHS.dashboard.cities}`}
        >
          Cities
        </SidebarButton>
      </If>
      <If condition={isShowUserManagement}>
        <SidebarButton
          icon={<UserManagementIcon />}
          width={'16px'}
          to={`${path}${PATHS.dashboard.userManagement}`}
        >
          User Management
        </SidebarButton>
      </If>
      <If condition={isShowDesignManagement}>
        <SidebarButton
          icon={<SidebarCreativeIcon />}
          width={'16px'}
          to={`${path}${PATHS.dashboard.designManagement}`}
        >
          Creative
        </SidebarButton>
      </If>
      <If condition={showHelpCenter}>
        <SidebarButton
          icon={<HelpOutlineIcon fontSize="large" />}
          to={{ pathname: `https://selfserve.oppizi.com/` }}
          target="_blank"
        >
          Help Center
        </SidebarButton>
      </If>
      <If condition={isShowSettings}>
        <SidebarButton
          icon={<SettingsIcon />}
          width={'16px'}
          to={`${path}${PATHS.dashboard.settings}`}
        >
          Settings
        </SidebarButton>
      </If>
      {/*  TODO DELETE WITH ALL OLD CHAT*/}
      {/*<If condition={isShowChat}>*/}
      {/*  <SidebarButton*/}
      {/*    icon={<ChatIcon />}*/}
      {/*    width={'22px'}*/}
      {/*    to={`${path}${PATHS.dashboard.chat}`}*/}
      {/*  >*/}
      {/*    <div style={{ display: 'flex', alignItems: 'center', float: 'left' }}>*/}
      {/*      Chat*/}
      {/*      <UnreadIndicator />*/}
      {/*    </div>*/}
      {/*  </SidebarButton>*/}
      {/*</If>*/}
      <If condition={isShowChatV2}>
        <SidebarButton
          icon={<ChatIcon />}
          width={'22px'}
          to={`${path}${PATHS.dashboard.chatV2}`}
        >
          <div style={{ display: 'flex', alignItems: 'center', float: 'left' }}>
            Chat
            {/* <UnreadIndicator /> */}
          </div>
        </SidebarButton>
      </If>
      <div className={classes.fixed} style={{ width: !isOpen && 64 }}>
        <div className={classes.version}>
          Version: {packageJson.releaseVersion}
        </div>
        <Sync className={classes.sync} />
      </div>
    </SidebarWrapper>
  )
}

export default injectStyles(styles)(Sidebar)
